import appManager from '@app/appManager';
import AppModule from '@app/lib/AppModule';
import { fetchTechnicians } from '@api/queries/getTechnicians';
import { TechniciansForTenantsResult } from '@api/generated/graphql';
import { sanitizePhoneNumberTo10Digits } from '@lib/utils/formatPhone/sanitizePhoneNumberTo10Digits';
import AuthModule from '@modules/AuthModule';
import {
  AuthEvents,
  AuthUser,
  IContactCenterTenant,
} from '@modules/AuthModule/types';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import {
  TechnicianCacheStore,
  TechnicianCache,
  CACHE_TTL_HOURS,
} from './technicianCache';
import type { TechnicianTableRow } from './types';
import { techniciansState } from './state';

export type { TechnicianTableRow };
export { CACHE_TTL_HOURS };
export { useTechnicians } from './useTechnicians';
export { techniciansState } from './state';

export const TechnicianEvents = {
  LOADED: 'technicians.loaded',
} as const;

export class TechniciansModule extends AppModule {
  private readonly cache = new TechnicianCacheStore();
  private isLoaded = false;
  private offSigninListener: (() => void) | null = null;
  private offFlagListener: (() => void) | null = null;
  override readonly events: string[] = [TechnicianEvents.LOADED];

  /**
   * Wires up the cache change listener and subscribes to auth/featureflags
   * events so technicians are prefetched as soon as the user is known.
   * Runs before all modules load, keeping workspace initialization unblocked.
   */
  override preload(): void {
    this.cache.on(this.onCacheChange);
    this.armSigninListener();
    this.app.events.on(AuthEvents.SIGNOUT, this.onLogout);
    this.offFlagListener = FeatureFlagModule.addFlagListener(
      FeatureFlagKeys.featureDisplayTechnicianConversationHistory,
      ({ hasValue, value }) => {
        if (hasValue && value && !this.isLoaded) this.loadOnDemand();
      },
    ).remove;
  }

  /**
   * Registers a one-shot onAll listener for the signin + featureflags pair.
   * Cancels any previously armed listener before registering a new one, so
   * re-arming on signout never accumulates stale handlers.
   * Prefetches eagerly when the FF is on; lazy load via loadOnDemand() otherwise.
   */
  private armSigninListener(): void {
    this.offSigninListener?.();
    this.offSigninListener = this.app.events.onAll(
      [AuthEvents.SIGNIN, 'featureflags.initialized'],
      (user: AuthUser) => {
        this.offSigninListener = null;
        if (
          FeatureFlagModule.isEnabled(
            FeatureFlagKeys.featureDisplayTechnicianConversationHistory,
          )
        ) {
          void this.prefetchTechnicians(user);
        }
      },
    );
  }

  /** Clears all state and re-arms the signin listener for the next session. */
  private readonly onLogout = (): void => {
    this.isLoaded = false;
    techniciansState.technicians = [];
    techniciansState.technicianById = new Map();
    techniciansState.techniciansLoading = false;
    techniciansState.techniciansLoadFailed = false;
    techniciansState.techniciansHasPartialError = false;
    this.armSigninListener();
  };

  /**
   * Receives cache change notifications (including cross-tab storage events)
   * and reflects them into Valtio state. Ignores events for other users.
   */
  private readonly onCacheChange = (
    cache: TechnicianCache | null,
    userId: string,
  ): void => {
    if (userId !== AuthModule.user?.id) return;
    const data = cache?.data ?? [];
    techniciansState.technicians = data;
    techniciansState.technicianById = new Map(data.map((t) => [t.id, t]));
  };

  /**
   * Triggers a technician load if one has not started yet.
   * Called by the Technicians tab on first render when the FF is off,
   * preserving the previous lazy-load behaviour.
   */
  public loadOnDemand(): void {
    if (techniciansState.techniciansLoading) return;
    const user = AuthModule.user;
    if (!user) return;
    void this.prefetchTechnicians(user);
  }

  /**
   * Loads technicians from cache if the entry is fresh and tenant-relevant,
   * otherwise performs a foreground fetch from the BFF.
   */
  private async prefetchTechnicians(user: AuthUser): Promise<void> {
    const tenantIds = user.org.tenants.map((t) => t.id);
    const tenantMap = Object.fromEntries(
      user.org.tenants.map((t) => [t.id, t]),
    );

    const cached = this.cache.read(user.id, tenantIds);

    if (cached) {
      techniciansState.technicians = cached.data;
      techniciansState.technicianById = new Map(
        cached.data.map((t) => [t.id, t]),
      );
      this.isLoaded = true;
      this.app.events.emit(TechnicianEvents.LOADED);
      return;
    }

    techniciansState.techniciansLoading = true;
    await this.fetchAndUpdate(user.id, tenantIds, tenantMap);
  }

  /**
   * Fetches technicians from the BFF, writes the result to cache (which
   * triggers onCacheChange and updates state), and sets error flags.
   * Failures are silent — the workspace remains functional via the keypad.
   * Always emits technicians.loaded so async callers are never left waiting.
   */
  private async fetchAndUpdate(
    userId: string,
    tenantIds: string[],
    tenantMap: Record<string, IContactCenterTenant>,
  ): Promise<void> {
    try {
      const result = await fetchTechnicians(tenantIds);
      if (AuthModule.user?.id !== userId) return;
      const rows = this.buildRows(result.data, tenantMap);
      this.cache.write(userId, rows, tenantIds);
      techniciansState.techniciansLoadFailed =
        rows.length === 0 && result.errors.length > 0;
      techniciansState.techniciansHasPartialError =
        rows.length > 0 && result.errors.length > 0;
    } catch {
      if (AuthModule.user?.id === userId) {
        techniciansState.techniciansLoadFailed = true;
      }
    } finally {
      if (AuthModule.user?.id === userId) {
        techniciansState.techniciansLoading = false;
        this.isLoaded = true;
        this.app.events.emit(TechnicianEvents.LOADED);
      }
    }
  }

  /**
   * Maps raw BFF response data into table rows. Falls back to tenant name
   * when companyName is absent; coerces null phone to empty string.
   */
  private buildRows(
    tenantData: TechniciansForTenantsResult['data'],
    tenantMap: Record<string, IContactCenterTenant>,
  ): TechnicianTableRow[] {
    return tenantData.flatMap((td) =>
      td.technicians.map((tech) => ({
        id: tech.id,
        name: tech.name,
        phone: tech.phone ?? '',
        tenantId: td.tenantId,
        tenantName:
          tenantMap[td.tenantId]?.companyName ??
          tenantMap[td.tenantId]?.name ??
          td.tenantId,
      })),
    );
  }

  /**
   * Resolves immediately if technicians have already loaded, otherwise waits
   * for the next technicians.loaded event on the app bus. A 10-second timeout
   * guard ensures callers are never silently hung if the event never fires
   * (e.g., FeatureFlagModule fails to initialize).
   */
  private waitForLoad(timeoutMs = 10_000): Promise<void> {
    if (this.isLoaded) return Promise.resolve();
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.app.events.off(TechnicianEvents.LOADED, handler);
        resolve();
      }, timeoutMs);
      const handler = () => {
        clearTimeout(timer);
        this.app.events.off(TechnicianEvents.LOADED, handler);
        resolve();
      };
      this.app.events.on(TechnicianEvents.LOADED, handler);
    });
  }

  /** Removes all event listeners and cleans up the cache store. */
  public destroy(): void {
    this.offSigninListener?.();
    this.offSigninListener = null;
    this.offFlagListener?.();
    this.offFlagListener = null;
    this.cache.destroy();
    this.app.events.off(AuthEvents.SIGNOUT, this.onLogout);
  }

  /**
   * Returns the technician whose stored phone matches the given value (both
   * normalized to 10 digits). When the cache is already loaded the result is
   * returned synchronously; when a load is still in progress a Promise is
   * returned that resolves once the load completes (or times out).
   */
  public findTechnicianByPhone(
    phone: string,
  ): TechnicianTableRow | undefined | Promise<TechnicianTableRow | undefined> {
    const normalized = sanitizePhoneNumberTo10Digits(phone);
    const lookup = () =>
      techniciansState.technicians.find(
        (t) => sanitizePhoneNumberTo10Digits(t.phone) === normalized,
      );
    if (this.isLoaded) return lookup();
    return this.waitForLoad().then(lookup);
  }
}

export default new TechniciansModule(
  appManager.events,
  appManager.logger('Technicians'),
);
